import api from './api/api';
import aiActionHandlerService from './ai-action-handler.service';
import aiCensusActionsService from './ai-census-actions.service';
import aiActionIntentClassifierService from './ai-action-intent-classifier.service';
import { logErrorToSentry } from '../utilis/sentry-logger';
import Cookies from 'js-cookie';
import { getAccountId, getFacilityId } from '../utilis/facilityStorage';
import { refreshManager } from './auth/refreshManager';

/**
 * Map backend LangGraph node names to user-facing "reasoning" labels
 * Shown in the AI chat thinking indicator while an answer is being generated
 */
export const STREAM_STAGE_LABELS = {
    widgetSelector: 'Understanding your question and choosing relevant widgets…',
    enrichWidgets: 'Loading widget details…',
    filterGeneration: 'Translating your question into filters…',
    dataFetcher: 'Querying patient records…',
    analysisDataFetcher: 'Preparing data for trend analysis…',
    parallelAnalysis: 'Analyzing trends, anomalies and flags…',
    summarization: 'Composing your answer…',
    generalResponse: 'Composing your answer…',
    errorHandler: 'Wrapping up…'
};

// Rough completion percentage per stage so the progress bar advances steadily
export const STREAM_STAGE_PROGRESS = {
    widgetSelector: 15,
    enrichWidgets: 30,
    filterGeneration: 45,
    dataFetcher: 70,
    analysisDataFetcher: 70,
    parallelAnalysis: 85,
    summarization: 92,
    generalResponse: 92,
    errorHandler: 98
};

/**
 * AI Chat Service for handling AI chat conversations
 * Supports both legacy endpoints and new AI Agent API
 */
class AIChatService {
    constructor() {
        this.currentPollingInterval = null;
        this.isAborted = false;
        // In-memory fallback (primary persistence is localStorage like conversationId)
        this.vectorStoreId = null;
        // Request deduplication
        this.currentRequestId = null;
        this.isProcessing = false;
    }

    // Local persistence similar to conversationId handling
    getVectorStoreKey(context) {
        try {
            const accountId = context?.accountId || context?.pageData?.accountId || 'anon';
            const pageType = context?.pageType || context?.pageData?.pageType || 'unknown';
            return `ai_vs_${accountId}_${pageType}`;
        } catch {
            return 'ai_vs_anon_unknown';
        }
    }

    loadVectorStoreId(context) {
        try {
            const key = this.getVectorStoreKey(context);
            const stored = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
            return stored || this.vectorStoreId || null;
        } catch {
            return this.vectorStoreId || null;
        }
    }

    saveVectorStoreId(context, vsId) {
        try {
            this.vectorStoreId = vsId;
            const key = this.getVectorStoreKey(context);
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(key, vsId);
            }
        } catch {
            // ignore storage errors
        }
    }

    /**
     * Abort the current AI request
     */
    abort() {
        this.isAborted = true;
        this.isProcessing = false; // Reset processing flag on abort
        if (this.currentPollingInterval) {
            clearInterval(this.currentPollingInterval);
            this.currentPollingInterval = null;
        }
        // Also close any active SSE connection
        if (this.currentEventSource) {
            this.currentEventSource.close();
            this.currentEventSource = null;
        }
    }
    /**
     * Send message to AI assistant with context (with streaming support)
     * @param {Object} params - Request parameters
     * @param {string} params.message - User message
     * @param {Object} params.context - Page context
     * @param {Array} params.conversationHistory - Previous conversation messages
     * @param {Object} params.state - Redux state
     * @param {Function} params.dispatch - Redux dispatch function
     * @param {string} params.conversationId - Optional existing conversation ID for faster responses
     * @param {Function} params.onChunk - Callback function for real-time chunk processing
     * @param {string} params.vectorStoreId - Optional vector store ID from page state
     * @param {Function} params.onVectorStoreIdUpdate - Callback to update vector store ID in page state
     * @param {Function} params.onProgress - Callback function for polling progress updates (pollCount, processingTime)
     * @returns {Promise<Object>} AI response
     */
    async sendMessage({ message, context, conversationHistory = [], state, dispatch, conversationId, onChunk, vectorStoreId, onVectorStoreIdUpdate, onProgress }) {
        try {
            // CRITICAL: Set page type in action classifier for correct card type mapping
            const pageType = context?.pageType || context?.pageData?.pageType || 'hospital';
            aiActionIntentClassifierService.setPageType(pageType);
            
            // CRITICAL: Prevent duplicate requests
            if (this.isProcessing) {
                throw new Error('A request is already in progress. Please wait for it to complete.');
            }
            
            // Mark as processing
            this.isProcessing = true;
            const requestId = Date.now();
            this.currentRequestId = requestId;
            
            // CRITICAL: Stop any existing polling before starting new request
            if (this.currentPollingInterval) {
                clearInterval(this.currentPollingInterval);
                this.currentPollingInterval = null;
            }
            
            // Reset abort state for new request
            this.isAborted = false;

            // Attach vectorStoreId to context from page state (not localStorage)
            if (vectorStoreId) {
                try {
                    context = context || {};
                    context.pageData = context.pageData || {};
                    if (!context.pageData.vectorStoreId) {
                        context.pageData.vectorStoreId = vectorStoreId;
                        context.pageData.usingVectorStore = true;
                    }
                } catch (e) {
                    // Ignore vectorStoreId attachment errors
                }
            }

            // Determine endpoint based on page type
            const endpointMap = {
                'deceased': '/api/ai-chat/deceased-assistant-start',
                'hospital': '/api/ai-chat/hospital-assistant-start',
                'communityTransfer': '/api/ai-chat/community-assistant-start',
                'admission': '/api/ai-chat/admission-assistant-start',
                'overall': '/api/ai-chat/overall-assistant-start',
                // Default to hospital endpoint for other pages until they get their own
                'default': '/api/ai-chat/hospital-assistant-start'
            };
            const endpoint = endpointMap[pageType] || endpointMap['default'];
            
            // Use axios instance for polling-based approach
            let response;
            try {
                response = await api.post(endpoint, {
                    message: message.trim(),
                    context,
                    conversationHistory,
                    conversationId
                });
            } catch (err) {
                // Handle 429 (limit exceeded) errors with user-friendly message
                if (err.response?.status === 429) {
                    const limitData = err.response?.data;
                    const errorMessage = limitData?.reason || 'Usage limit exceeded';
                    const limit = limitData?.limit || 'unknown';
                    const used = limitData?.used || 'unknown';
                    const remaining = limitData?.remaining || 0;
                    
                    logErrorToSentry('AI Chat: Rate limit exceeded', {
                        service: 'ai-chat',
                        event: 'rate_limit',
                        pageType,
                        extra: {
                            requestId,
                            endpoint,
                            limit,
                            used,
                            remaining,
                            reason: errorMessage
                        }
                    });
                    
                    throw new Error(
                        `🚫 ${errorMessage}\n\n` +
                        `You have used ${used} out of ${limit} prompts this month.\n` +
                        `Remaining: ${remaining} prompts.\n\n` +
                        `Your limit will reset at the start of next month.`
                    );
                }
                
                // Log other API errors
                logErrorToSentry(err, {
                    service: 'ai-chat',
                    event: 'api_error',
                    pageType,
                    extra: {
                        requestId,
                        endpoint,
                        statusCode: err.response?.status,
                        errorMessage: err.message
                    }
                });
                
                // Re-throw other errors
                throw err;
            }

            const data = response || {};
            
            // CRITICAL: Validate taskId exists
            if (!data.taskId) {
                logErrorToSentry('AI Chat: No taskId received from backend', {
                    service: 'ai-chat',
                    event: 'missing_taskid',
                    pageType,
                    extra: {
                        requestId,
                        endpoint,
                        responseData: data
                    }
                });
                throw new Error('No taskId received from backend');
            }
            
            const pollTaskResult = async (taskId) => {
                const maxWait = 22 * 60 * 1000; // 22 minutes (longer than backend's 20-minute timeout for GPT-5 Pro reasoning)
                const start = Date.now();
                let consecutiveNotFoundCount = 0;
                const maxNotFoundRetries = 7; // Increased from 5 to 7 for slower task initialization
                let intervalId = null;
                
                // CRITICAL: Add initial delay to give backend time to initialize task
                const isFastMode = context?.reasoningMode?.label === 'Fast';
                await new Promise(resolve => setTimeout(resolve, isFastMode ? 100 : 300));

                return new Promise((resolve, reject) => {
                    // Helper to safely clear interval and reset state
                    const safeCleanup = () => {
                        if (intervalId) {
                            clearInterval(intervalId);
                            intervalId = null;
                        }
                        if (this.currentPollingInterval) {
                            clearInterval(this.currentPollingInterval);
                            this.currentPollingInterval = null;
                        }
                    };
                    
                    try {
                        intervalId = setInterval(async () => {
                            try {
                                // Check if request was aborted
                                if (this.isAborted) {
                                    safeCleanup();
                                    return reject(new Error('Request aborted by user'));
                                }
                                
                                if (Date.now() - start > maxWait) {
                                    const elapsedTime = Math.round((Date.now() - start) / 1000);
                                    logErrorToSentry('AI Chat: Polling timeout exceeded', {
                                        service: 'ai-chat',
                                        event: 'polling_timeout',
                                        pageType,
                                        extra: {
                                            taskId,
                                            requestId,
                                            elapsedTimeSeconds: elapsedTime,
                                            maxWaitSeconds: Math.round(maxWait / 1000)
                                        }
                                    });
                                    safeCleanup();
                                    return reject(new Error('AI processing timed out. Please try again.'));
                                }
                                
                                // Determine polling endpoint based on page type
                                const pollingEndpointMap = {
                                    'deceased': `/api/ai-chat/deceased-assistant/${taskId}`,
                                    'hospital': `/api/ai-chat/hospital-assistant/${taskId}`,
                                    'communityTransfer': `/api/ai-chat/community-assistant/${taskId}`,
                                    'admission': `/api/ai-chat/admission-assistant/${taskId}`,
                                    'overall': `/api/ai-chat/overall-assistant/${taskId}`,
                                    'default': `/api/ai-chat/hospital-assistant/${taskId}`
                                };
                                const pollingEndpoint = pollingEndpointMap[pageType] || pollingEndpointMap['default'];
                                
                                const response = await api.get(pollingEndpoint);
                                const data = response || {};

                                // Reset 404 counter on successful response
                                consecutiveNotFoundCount = 0;

                                if (data.status === 'complete') {
                                    safeCleanup();
                                    
                    // ⚡ FAST ACTION DETECTION: Check if this is a fast action from the new classifier
                    // CRITICAL: Filter actions should NOT be auto-executed, they should show as buttons
                    if (data.finalResponse?.isAction && data.finalResponse?.shouldExecuteOnFrontend && data.classification && data.actionParams) {
                        const actionIntent = data.classification?.intent;
                        
                        // IMPORTANT: Filter actions should NOT be auto-executed, they need user confirmation
                        // Only setup_alert and change_date use fast path with auto-execution
                        const shouldAutoExecute = actionIntent !== 'apply_filter' && actionIntent !== 'filter_action';
                        
                        if (shouldAutoExecute) {
                            // Format as executedAction for the existing system
                            const fastAction = {
                                success: true,
                                action: 'fast_action',
                                classification: data.classification,
                                actionParams: data.actionParams,
                                message: data.finalResponse.response
                            };
                            
                            // Return response with the action in executedActions array
                            resolve({
                                success: true,
                                data: {
                                    ...data.finalResponse,
                                    executedActions: [fastAction], // Will be executed by executeBackendActions
                                    hasExecutedActions: true
                                }
                            });
                        } else {
                            // Filter actions: Return as normal response with availableActions for buttons
                            resolve({ success: true, data: data.finalResponse });
                        }
                    } else {
                        // Normal AI response (no fast action)
                        resolve({ success: true, data: data.finalResponse });
                    }
                                } else if (data.status === 'failed') {
                                    const pollCount = data.pollCount || 0;
                                    const failureReason = data.result || 'Unknown error';
                                    
                                    logErrorToSentry('AI Chat: Request failed on backend', {
                                        service: 'ai-chat',
                                        event: 'backend_failure',
                                        pageType,
                                        extra: {
                                            taskId,
                                            requestId,
                                            pollCount,
                                            failureReason,
                                            processingTime: data.processingTime
                                        }
                                    });
                                    
                                    safeCleanup();
                                    
                                    // Provide user-friendly error messages
                                    let errorMessage = data.result || 'AI processing failed';
                                    if (errorMessage.includes('token') || errorMessage.includes('limit')) {
                                        errorMessage = 'The AI request exceeded token limits. Please try with a shorter message or fewer filters.';
                                    } else if (errorMessage.includes('timeout') || errorMessage.includes('exceeded')) {
                                        errorMessage = 'AI processing timed out. Please try again with a simpler request.';
                                    } else if (errorMessage.includes('model') || errorMessage.includes('fallback')) {
                                        errorMessage = 'AI service encountered an error. Please try again.';
                                    }
                                    
                                    reject(new Error(errorMessage));
                                } else {
                                    // Call progress callback with real backend data
                                    if (onProgress) {
                                        onProgress({
                                            pollCount: data.pollCount || 0,
                                            processingTime: data.processingTime || 0, // seconds
                                            status: data.status,
                                            reasoningDetails: data.reasoningDetails || null // If backend provides this
                                        });
                                    }
                                }
                                // If status is still 'processing', continue polling

                            } catch (err) {
                                // Handle 404 "Task not found" errors with improved retry logic
                                if (err.response?.status === 404) {
                                    consecutiveNotFoundCount++;
                                    
                                    if (consecutiveNotFoundCount >= maxNotFoundRetries) {
                                        logErrorToSentry('AI Chat: Task not found after max retries', {
                                            service: 'ai-chat',
                                            event: 'task_not_found',
                                            pageType,
                                            extra: {
                                                taskId,
                                                requestId,
                                                consecutiveNotFoundCount,
                                                maxNotFoundRetries,
                                                availableTasks: err.response?.data?.availableTasks
                                            }
                                        });
                                        
                                        safeCleanup();
                                        return reject(new Error(
                                            err.response?.data?.message || 
                                            'AI processing task not found. The task may have been cleaned up due to server restart or timeout. Please try your request again.'
                                        ));
                                    }
                                    
                                    // Continue polling if under retry limit
                                    return;
                                }
                                
                                // Handle network errors - retry a few times
                                if (!err.response) {
                                    // Continue polling for network errors (don't give up immediately)
                                    return;
                                }
                                
                                // For other errors, stop polling immediately
                                logErrorToSentry(err, {
                                    service: 'ai-chat',
                                    event: 'polling_error',
                                    pageType,
                                    extra: {
                                        taskId,
                                        requestId,
                                        statusCode: err.response?.status,
                                        errorMessage: err.message,
                                        responseData: err.response?.data
                                    }
                                });
                                
                                safeCleanup();
                                reject(new Error(err.response?.data?.message || err.message || 'Failed to get AI response'));
                            }
                        }, isFastMode ? 500 : 2000); // Poll faster for Fast mode
                        
                        // Store interval ID for cleanup
                        this.currentPollingInterval = intervalId;
                    } catch (error) {
                        logErrorToSentry(error, {
                            service: 'ai-chat',
                            event: 'polling_setup_error',
                            pageType,
                            extra: {
                                taskId,
                                requestId
                            }
                        });
                        safeCleanup();
                        reject(error);
                    }
                });
            };

            const finalResult = await pollTaskResult(data.taskId);

            if (finalResult?.success) {
                const responseData = finalResult.data;
                
                // Capture vectorStoreId from backend and update page state
                if (responseData?.vectorStoreId) {
                    // Update page state via callback
                    if (onVectorStoreIdUpdate) {
                        onVectorStoreIdUpdate(responseData.vectorStoreId);
                    }
                    // Also save to localStorage as fallback
                    this.saveVectorStoreId(context, responseData.vectorStoreId);
                }
                
                // Process AI response for actions
                const processedResponse = aiActionHandlerService.processAIResponse(responseData.response);
                
                // Execute any actions that were detected and executed by the backend
                if (responseData.executedActions && responseData.executedActions.length > 0) {
                    await this.executeBackendActions(responseData.executedActions, state, dispatch);
                }
                
                return {
                    ...responseData,
                    ...processedResponse,
                    // Make sure availableActions are included in the response
                    availableActions: responseData.availableActions || []
                };
            } else {
                throw new Error('AI service returned an invalid response');
            }
        } catch (error) {
            // Log error with context
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'send_message_error',
                pageType: context?.pageType || context?.pageData?.pageType || 'unknown',
                extra: {
                    requestId: this.currentRequestId,
                    hasConversationId: !!conversationId,
                    conversationHistoryLength: conversationHistory.length,
                    errorMessage: error.message,
                    isOnline: navigator.onLine
                }
            });
            
            // Handle specific error cases
            if (error.message.includes('503')) {
                throw new Error('AI service temporarily unavailable. Please try again later.');
            } else if (error.message.includes('400')) {
                throw new Error('Invalid request parameters');
            } else if (error.message.includes('401')) {
                throw new Error('Authentication required. Please log in again.');
            } else if (error.message.includes('500')) {
                throw new Error('Server error. Please try again later.');
            } else if (!navigator.onLine) {
                throw new Error('Network error. Please check your connection and try again.');
            } else {
                throw new Error(error.message || 'Failed to get AI response');
            }
        } finally {
            // CRITICAL: Always clear processing flag when request completes
            this.isProcessing = false;
        }
    }

    /**
     * Send message to AI Agent API (new multi-agent system)
     * This method is designed for widget Q&A and uses the new AI agent backend
     *
     * @param {Object} params - Request parameters
     * @param {string} params.message - User message
     * @param {Object} params.context - Page context
     * @param {Array} params.conversationHistory - Previous conversation messages
     * @param {string} params.conversationId - Optional existing conversation ID
     * @param {string} params.startDate - Start date from dashboard filter (ISO string)
     * @param {string} params.endDate - End date from dashboard filter (ISO string)
     * @returns {Promise<Object>} AI response
     */
    async sendAgentMessage({ message, context, conversationHistory = [], conversationId, startDate, endDate, comparisonStartDate, comparisonEndDate, mode = 'trend' }) {
        try {
            const pageType = context?.pageType || context?.pageData?.pageType || 'hospital';

            // Call the new AI agent API endpoint
            const response = await api.post('/api/ai-agent/chat', {
                message: message.trim(),
                conversationId,
                facilityIds: context?.facilityIds || [],
                pageContext: {
                    pageType: pageType
                },
                startDate,  // Always from dashboard filter
                endDate,    // Always from dashboard filter
                comparisonStartDate,  // From comparison date picker
                comparisonEndDate,    // From comparison date picker
                mode          // 'trend' (default LangGraph) or 'clinical' (clinical analysis agent)
            });

            // Map the response to the existing handler format
            return this.mapAgentResponse(response);
        } catch (error) {
            // Handle 429 (limit exceeded) errors with user-friendly message
            if (error.response?.status === 429) {
                const limitData = error.response?.data;
                const errorMessage = limitData?.reason || 'Usage limit exceeded';
                const limit = limitData?.limit || 'unknown';
                const used = limitData?.used || 'unknown';
                const remaining = limitData?.remaining || 0;

                logErrorToSentry('AI Agent: Rate limit exceeded', {
                    service: 'ai-agent',
                    event: 'rate_limit',
                    extra: {
                        endpoint: '/api/ai-agent/chat',
                        limit,
                        used,
                        remaining,
                        reason: errorMessage
                    }
                });

                throw new Error(
                    `🚫 ${errorMessage}\n\n` +
                    `You have used ${used} out of ${limit} prompts this month.\n` +
                    `Remaining: ${remaining} prompts.\n\n` +
                    `Your limit will reset at the start of next month.`
                );
            }

            // Log other API errors
            logErrorToSentry(error, {
                service: 'ai-agent',
                event: 'api_error',
                extra: {
                    endpoint: '/api/ai-agent/chat',
                    statusCode: error.response?.status,
                    errorMessage: error.message
                }
            });

            throw error;
        }
    }

    /**
     * Send message to AI Agent API with Server-Sent Events streaming.
     * The graph runs identically to /api/ai-agent/chat — only the transport differs,
     * so the final answer, actions, cardData and metadata are the same.
     *
     * @param {Object} params
     * @param {Function} params.onStage  - called with { node, label, progress } as each stage starts
     * @param {Function} params.onNodeComplete - called with the raw node_complete event
     * @param {Function} params.onComplete - called once with the mapped final response
     *                                       (same shape as sendAgentMessage's return value)
     * @param {Function} params.onError   - called on error; err.isRateLimit=true for 429
     * @param {AbortSignal} params.signal - optional abort signal
     * @returns {Promise<Object>} Mapped final response (same as sendAgentMessage)
     */
    async sendAgentMessageStream({
        message,
        context,
        conversationId,
        startDate,
        endDate,
        comparisonStartDate,
        comparisonEndDate,
        mode = 'trend',
        onStage,
        onNodeComplete,
        onConversation,
        onToken,
        onSummaryEnd,
        onComplete,
        onError,
        signal
    }) {
        const pageType = context?.pageType || context?.pageData?.pageType || 'hospital';
        const baseURL = process.env.REACT_APP_BASE_URL || '';
        const url = `${baseURL}/api/ai-agent/chat/stream`;

        // Mirror the headers that axios's request interceptor adds. Built fresh
        // on each attempt so a post-refresh retry picks up the new token.
        const buildHeaders = () => {
            const idToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('x-auth') : null;
            const accountId = getAccountId();
            const facilityId = getFacilityId();
            const csrfToken = Cookies.get('__Secure-CSRF-Token') || Cookies.get('CSRF-Token');

            const headers = {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream'
            };
            if (idToken) headers['x-auth'] = idToken;
            if (accountId) headers.accountId = accountId;
            if (facilityId) headers.facilityId = facilityId;
            if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
            return headers;
        };

        const body = JSON.stringify({
            message: message.trim(),
            conversationId,
            facilityIds: context?.facilityIds || [],
            pageContext: { pageType },
            startDate,
            endDate,
            comparisonStartDate,
            comparisonEndDate,
            mode
        });

        const doFetch = () => fetch(url, {
            method: 'POST',
            headers: buildHeaders(),
            body,
            credentials: 'include',
            signal
        });

        const stringifyErr = (val, fallback) => {
            if (val == null) return fallback;
            if (typeof val === 'string') return val;
            if (typeof val === 'object') {
                if (typeof val.message === 'string') return val.message;
                try { return JSON.stringify(val); } catch { return fallback; }
            }
            return String(val);
        };

        let response;
        try {
            response = await doFetch();
        } catch (err) {
            if (err?.name === 'AbortError') throw err;
            const wrapped = new Error(err?.message || 'Network error while streaming AI response');
            wrapped.isNetworkError = true;
            if (onError) onError(wrapped);
            throw wrapped;
        }

        if (response.status === 401) {
            let retried = false;
            try {
                await refreshManager.refresh();
                response = await doFetch();
                retried = true;
            } catch (refreshErr) {
                const authErr = new Error('Your session has expired. Please log in again.');
                authErr.isNetworkError = true;
                authErr.status = 401;
                if (onError) onError(authErr);
                throw authErr;
            }
            if (retried && response.status === 401) {
                const authErr = new Error('Your session has expired. Please log in again.');
                authErr.isNetworkError = true;
                authErr.status = 401;
                if (onError) onError(authErr);
                throw authErr;
            }
        }

        if (!response.ok) {
            // Drain JSON body for rate limit / structured error reporting
            let errData = null;
            try { errData = await response.json(); } catch { /* ignore */ }

            if (response.status === 429) {
                const reason = errData?.reason || 'Usage limit exceeded';
                const limit = errData?.limit || 'unknown';
                const used = errData?.used || 'unknown';
                const remaining = errData?.remaining || 0;
                logErrorToSentry('AI Agent: Rate limit exceeded (stream)', {
                    service: 'ai-agent',
                    event: 'rate_limit',
                    extra: { endpoint: '/api/ai-agent/chat/stream', limit, used, remaining, reason }
                });
                const rateErr = new Error(
                    `🚫 ${reason}\n\n` +
                    `You have used ${used} out of ${limit} prompts this month.\n` +
                    `Remaining: ${remaining} prompts.\n\n` +
                    `Your limit will reset at the start of next month.`
                );
                rateErr.isRateLimit = true;
                if (onError) onError(rateErr);
                throw rateErr;
            }

            const httpErr = new Error(stringifyErr(errData?.error, `Stream request failed (${response.status})`));
            httpErr.isNetworkError = true;
            httpErr.status = response.status;
            if (onError) onError(httpErr);
            throw httpErr;
        }

        if (!response.body?.getReader) {
            const err = new Error('Streaming not supported in this environment');
            err.isNetworkError = true;
            if (onError) onError(err);
            throw err;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let mappedFinal = null;

        try {
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                // SSE events are delimited by a blank line
                let sepIdx;
                while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
                    const rawEvent = buffer.slice(0, sepIdx);
                    buffer = buffer.slice(sepIdx + 2);

                    // A single SSE event can have multiple `data:` lines
                    const dataLines = rawEvent
                        .split('\n')
                        .filter(line => line.startsWith('data:'))
                        .map(line => line.slice(5).trimStart());

                    if (dataLines.length === 0) continue;
                    const payload = dataLines.join('\n');
                    if (payload === '[DONE]') continue;

                    let evt;
                    try {
                        evt = JSON.parse(payload);
                    } catch {
                        continue;
                    }

                    switch (evt.type) {
                        case 'conversation':
                            if (onConversation) onConversation(evt.conversationId);
                            break;
                        case 'start':
                        case 'stage': {
                            if (onStage) {
                                onStage({
                                    node: evt.node,
                                    label: STREAM_STAGE_LABELS[evt.node] || 'Working on it…',
                                    progress: STREAM_STAGE_PROGRESS[evt.node] ?? 10
                                });
                            }
                            break;
                        }
                        case 'node_complete':
                            if (onNodeComplete) onNodeComplete(evt);
                            break;
                        case 'token':
                            if (onToken && evt.text) onToken(evt.text);
                            break;
                        case 'summary_end':
                            if (onSummaryEnd) onSummaryEnd();
                            break;
                        case 'complete':
                            mappedFinal = this.mapAgentResponse({ data: evt.result });
                            if (onComplete) onComplete(mappedFinal);
                            break;
                        case 'error': {
                            const streamErr = new Error(evt.error || 'Streaming error');
                            streamErr.isStreamError = true;
                            if (onError) onError(streamErr);
                            throw streamErr;
                        }
                        case 'aborted':
                            // Remote side acknowledged abort — no further events will come
                            break;
                        case 'saved':
                            // Final persistence confirmation — nothing to do on the UI
                            break;
                        default:
                            break;
                    }
                }
            }
        } finally {
            try { reader.releaseLock?.(); } catch { /* ignore */ }
        }

        if (!mappedFinal) {
            const err = new Error('Stream ended before a complete event was received');
            err.isNetworkError = true;
            if (onError) onError(err);
            throw err;
        }

        return mappedFinal;
    }

    /**
     * Notify the backend that the user pressed Stop, along with the exact
     * content the frontend had on screen at that moment. Backend overwrites
     * the persisted assistant message with this content so reopening the
     * conversation shows exactly what the user saw.
     *
     * Silent-failure by design — the stream has already been aborted, so we
     * don't want this follow-up to surface errors to the user.
     */
    async recordStoppedReply({ conversationId, content }) {
        if (!conversationId) return;
        try {
            await api.post('/api/ai-agent/chat/stop', {
                conversationId,
                content: content || ''
            });
        } catch (err) {
            logErrorToSentry('AI Agent: stop record failed', {
                service: 'ai-agent',
                event: 'stop_record_error',
                extra: {
                    endpoint: '/api/ai-agent/chat/stop',
                    message: err?.message
                }
            });
        }
    }

    /**
     * Map AI Agent API response to existing handler format
     * @param {Object} response - Raw API response
     * @returns {Object} Mapped response
     */
    mapAgentResponse(response) {
        const data = response?.data || response;

        return {
            success: true,
            data: {
                response: data.response,
                conversationId: data.conversationId,
                // Map actions to availableActions for existing button rendering system
                availableActions: data.actions || [],
                metadata: data.metadata,
                cardData: data.cardData
            }
        };
    }

    /**
     * Simplified method for preloading conversation context (used by Hospital.js)
     * @param {string} message - Preload message
     * @param {Object} context - Page context
     * @returns {Promise<Object>} Response with conversation ID
     */
    async preloadConversation(message, context) {
        return this.sendMessage({
            message,
            context,
            conversationHistory: [],
            state: null,
            dispatch: null
        });
    }

    /**
     * Get conversation history
     * @param {string} conversationId - Conversation ID
     * @returns {Promise<Object>} Conversation and messages
     */
    async getConversationHistory(conversationId) {
        try {
            const response = await api.get(`/api/ai-chat/conversation/${conversationId}`);

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to get conversation history');
            }
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'get_conversation_history_error',
                extra: {
                    conversationId,
                    statusCode: error.response?.status
                }
            });
            
            if (error.response?.status === 404) {
                throw new Error('Conversation not found');
            } else if (error.response?.status === 403) {
                throw new Error('Access denied to this conversation');
            } else {
                throw new Error(error.message || 'Failed to get conversation history');
            }
        }
    }

    /**
     * Get user's conversations
     * @param {Object} options - Query options
     * @param {string} options.pageType - Page type filter
     * @param {number} options.limit - Number of conversations to return
     * @param {number} options.offset - Number of conversations to skip
     * @returns {Promise<Object>} Conversations list
     */
    async getUserConversations({ pageType, limit = 20, offset = 0 } = {}) {
        try {
            const params = new URLSearchParams();
            if (pageType) params.append('pageType', pageType);
            if (limit) params.append('limit', limit.toString());
            if (offset) params.append('offset', offset.toString());

            const response = await api.get(`/api/ai-chat/conversations?${params.toString()}`);

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to get conversations');
            }
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'get_user_conversations_error',
                extra: {
                    pageType,
                    limit,
                    offset,
                    statusCode: error.response?.status
                }
            });
            
            if (error.response?.status === 401) {
                throw new Error('Authentication required. Please log in again.');
            } else {
                throw new Error(error.message || 'Failed to get conversations');
            }
        }
    }

    /**
     * Delete conversation
     * @param {string} conversationId - Conversation ID
     * @returns {Promise<Object>} Success response
     */
    async deleteConversation(conversationId) {
        try {
            const response = await api.delete(`/api/ai-chat/conversation/${conversationId}`);

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to delete conversation');
            }
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'delete_conversation_error',
                extra: {
                    conversationId,
                    statusCode: error.response?.status
                }
            });
            
            if (error.response?.status === 404) {
                throw new Error('Conversation not found');
            } else if (error.response?.status === 403) {
                throw new Error('Access denied to this conversation');
            } else {
                throw new Error(error.message || 'Failed to delete conversation');
            }
        }
    }

    /**
     * Delete only AI messages from a conversation (keep conversation and user messages)
     * @param {string} conversationId - Conversation ID
     * @returns {Promise<Object>} Success response with deleted count
     */
    async deleteAIMessages(conversationId) {
        try {
            const response = await api.delete(`/api/ai-chat/conversation/${conversationId}/messages`);

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to delete AI messages');
            }
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'delete_ai_messages_error',
                extra: {
                    conversationId,
                    statusCode: error.response?.status
                }
            });
            
            if (error.response?.status === 404) {
                throw new Error('Conversation not found');
            } else if (error.response?.status === 403) {
                throw new Error('Access denied to this conversation');
            } else {
                throw new Error(error.message || 'Failed to delete AI messages');
            }
        }
    }

    /**
     * Record feedback on AI response
     * @param {Object} params - Feedback parameters
     * @param {string} params.messageId - Message ID
     * @param {string} params.feedback - Feedback type ('positive' or 'negative')
     * @param {string} params.comment - Optional comment
     * @returns {Promise<Object>} Success response
     */
    async recordFeedback({ messageId, feedback, comment = '' }) {
        try {
            const response = await api.post('/api/ai-chat/feedback', {
                messageId,
                feedback,
                comment: comment.trim()
            });

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to record feedback');
            }
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'record_feedback_error',
                extra: {
                    messageId,
                    feedback,
                    statusCode: error.response?.status
                }
            });
            
            if (error.response?.status === 400) {
                throw new Error(error.response.data?.error || 'Invalid feedback parameters');
            } else if (error.response?.status === 404) {
                throw new Error('Message not found');
            } else {
                throw new Error(error.message || 'Failed to record feedback');
            }
        }
    }

    /**
     * Create context object for AI chat
     * @param {Object} params - Context parameters
     * @param {string} params.pageType - Page type
     * @param {Object} params.pageData - Page data
     * @param {Array} params.facilityIds - Active facility IDs
     * @param {string} params.accountId - Account ID
     * @param {string} params.conversationId - Existing conversation ID
     * @param {string} params.vectorStoreId - Optional vector store ID from page state
     * @param {Object} params.reasoningMode - Optional reasoning mode configuration
     * @param {string} params.startDate - Optional start date from dashboard filter (ISO string)
     * @param {string} params.endDate - Optional end date from dashboard filter (ISO string)
     * @returns {Object} Formatted context object
     */
    createContext({ pageType, pageData = {}, facilityIds = [], accountId, conversationId, vectorStoreId, reasoningMode, startDate, endDate }) {
        const context = {
            pageType,
            pageData,
            facilityIds,
            accountId,
            conversationId,
            timestamp: new Date().toISOString()
        };

        // Include vectorStoreId if provided
        if (vectorStoreId) {
            context.pageData = {
                ...context.pageData,
                vectorStoreId,
                usingVectorStore: true
            };
        }

        // Include reasoningMode if provided
        if (reasoningMode) {
            context.reasoningMode = reasoningMode;
        }

        // Include date range if provided (from dashboard filter)
        if (startDate) {
            context.startDate = startDate;
        }
        if (endDate) {
            context.endDate = endDate;
        }

        return context;
    }

    /**
     * Format conversation history for API
     * @param {Array} messages - Array of message objects
     * @param {number} limit - Maximum number of messages to include
     * @returns {Array} Formatted conversation history
     */
    formatConversationHistory(messages, limit = 10) {
        // Filter out validation messages and their preceding user messages
        const filteredMessages = [];
        for (let i = 0; i < messages.length; i++) {
            const msg = messages[i];
            const nextMsg = messages[i + 1];
            
            // Skip user messages that triggered validation responses
            if (msg.type === 'user' && nextMsg?.isValidationMessage) {
                i++; // Also skip the next message (the validation response)
                continue;
            }
            
            // Skip validation messages
            if (msg.isValidationMessage) {
                continue;
            }
            
            filteredMessages.push(msg);
        }
        
        return filteredMessages
            .slice(-limit)
            .map(msg => ({
                type: msg.type,
                content: msg.content,
                timestamp: msg.timestamp
            }));
    }

    /**
     * Execute an AI action
     * @param {Object} action - Action to execute
     * @returns {Promise<Object>} Execution result
     */
    async executeAction(action) {
        try {
            const result = await aiActionHandlerService.executeAction(action);
            return result;
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'execute_action_error',
                extra: {
                    actionType: action?.type,
                    action: action
                }
            });
            throw new Error(error.message || 'Failed to execute action');
        }
    }

    /**
     * Get available actions for current state
     * @returns {Array} Available actions
     */
    getAvailableActions() {
        return aiActionHandlerService.getAvailableActions();
    }

    /**
     * Generate action suggestions based on user message
     * @param {string} message - User message
     * @returns {Array} Action suggestions
     */
    generateActionSuggestions(message) {
        return aiActionHandlerService.generateActionSuggestions(message);
    }

    /**
     * Execute actions that were detected and prepared by the backend
     * @param {Array} executedActions - Actions to execute
     * @param {Object} state - Current Redux state
     * @param {Function} dispatch - Redux dispatch function
     * @returns {Promise<void>}
     */
    async executeBackendActions(executedActions, state, dispatch) {
        try {
            // Initialize the census service with state and dispatch
            // Note: state can be undefined in some contexts, so handle gracefully
            if (dispatch && typeof state !== 'undefined') {
                aiCensusActionsService.initialize(dispatch, () => state);
            }

            for (const action of executedActions) {
                if (action.success) {
                    switch (action.action) {
                        case 'switch_percentage_method':
                            // Execute the actual frontend action
                            aiCensusActionsService.changePercentageMethod(action.method);
                            break;
                            
                        case 'add_custom_percentage':
                            // Execute custom percentage addition
                            if (action.customData) {
                                aiCensusActionsService.addCustomPercentage(action.customData);
                            }
                            break;
                            
                        case 'remove_custom_percentage':
                            // Execute custom percentage removal
                            if (action.index !== undefined) {
                                aiCensusActionsService.removeCustomPercentage(action.index);
                            }
                            break;
                            
                        case 'open_chart_dialog':
                        case 'open_residence_dialog':
                        case 'open_building_dialog':
                            // Execute dialog actions automatically
                            if (action.actionData && action.autoExecuted) {
                                await aiActionHandlerService.executeAction(action.actionData);
                            }
                            break;
                        
                        // ⚡ NEW: Handle fast action execution from smart classifier
                        case 'fast_action':
                            if (action.classification && action.actionParams) {
                                await aiActionIntentClassifierService.executeAction(
                                    action.classification,
                                    action.actionParams,
                                    { state, dispatch }
                                );
                            }
                            break;
                            
                        default:
                            // Unhandled backend action
                            break;
                    }
                }
            }
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'execute_backend_actions_error',
                extra: {
                    executedActionsCount: executedActions?.length,
                    actions: executedActions?.map(a => ({ action: a.action, success: a.success }))
                }
            });
        }
    }

    /**
     * Execute a clicked date action (when user clicks on action button)
     * @param {Object} actionData - Action data from clicked button
     * @returns {Promise<Object>} Execution result
     */
    async executeClickedDateAction(actionData) {
        try {
            const result = await aiActionHandlerService.executeClickedDateAction(actionData);
            return result;
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-chat',
                event: 'execute_clicked_date_action_error',
                extra: {
                    actionData
                }
            });
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Check if message is an automated report setup request
     * @param {string} message - User message
     * @param {Array} conversationHistory - Conversation history to check for ongoing setup
     * @returns {boolean} Is automated report request
     */
    isAutomatedReportRequest(message, conversationHistory = []) {
        const lowerMessage = message.toLowerCase().trim();
        
        // Check for initial automated report request
        const isInitialRequest = (lowerMessage.includes('set up') || lowerMessage.includes('setup') || lowerMessage.includes('create')) &&
               (lowerMessage.includes('automated report') || lowerMessage.includes('automatic report') || lowerMessage.includes('report')) &&
               !lowerMessage.includes('alert') &&
               !lowerMessage.includes('generate'); // Exclude generate report
        
        if (isInitialRequest) {
            return true;
        }
        
        // Since we now directly open dialog from frequency selection, 
        // we don't need to handle ongoing conversations for automated reports
        
        return false;
    }

    /**
     * Check if message is an automated alert setup request
     * @param {string} message - User message
     * @returns {boolean} Is automated alert request
     */
    isAutomatedAlertRequest(message) {
        const lowerMessage = message.toLowerCase().trim();
        return (lowerMessage.includes('set up') || lowerMessage.includes('setup') || lowerMessage.includes('create')) &&
               (lowerMessage.includes('automated alert') || lowerMessage.includes('automatic alert') || lowerMessage.includes('alert'));
    }

    // Removed: handleAutomatedReportSetup - All report handling is now done by backend AI

    // Removed: handleAutomatedAlertSetup - All alert handling is now done by backend AI

    // Removed: extractAutomatedReportContext and extractAutomatedAlertContext
    // All context extraction is now handled by the backend AI

    /**
     * Extract card type from message
     * @param {string} message - User message
     * @returns {string|null} Card type
     */
    extractCardTypeFromMessage(message) {
        const lowerMessage = message.toLowerCase();
        
        // Card type mappings using actual HOSPITAL_CARDS_TYPE constants
        const cardMappings = {
            'hospitalizations': ['hospitalization', 'new hospitalization', 're-hospitalization', 'admission', 'new hospitalizations', 're-hospitalizations'],
            'DCERData': ['dc', 'er', 'discharge', 'emergency', 'dc/er', 'dc er', 'dcer', 'dc / er'],
            'insuranceData': ['insurance', 'payer', 'insurances', 'per insurance'],
            'returnsData': ['return', 'returned', 'didnt return', 'didn\'t return', 'returned / didn\'t return'],
            'ninetyDaysData': ['90 days', 'ninety days', 'length of stay', '90 days analysis'],
            'floorsData': ['floor', 'floors', 'unit', 'units', 'per floor'],
            'doctorData': ['doctor', 'doctors', 'physician', 'physicians', 'per doctor'],
            'daysData': ['days', 'day', 'weekday', 'weekdays', 'per day', 'day of the week'],
            'dxData': ['diagnosis', 'diagnoses', 'dx', 'per diagnosis'],
            'shiftData': ['shift', 'shifts', 'per shift'],
            'nurseData': ['nurse', 'nurses', 'nursing', 'per nurse'],
            'hospitalData': ['hospital', 'hospitals', 'facility', 'facilities', 'hospitals transferred to', 'hospitals residents transferred to'],
            'unplannedHospitalTransfer': ['unplanned', 'unplanned transfer', 'unplanned transfers', 'unplanned hospital transfer'],
            'plannedHospitalTransfer': ['planned', 'planned transfer', 'planned transfers', 'planned hospital transfer'],
            'total': ['total', 'overall', 'all data', 'total data']
        };

        // Check in order of specificity (most specific first)
        for (const [cardType, aliases] of Object.entries(cardMappings)) {
            if (aliases.some(alias => lowerMessage.includes(alias))) {
                return cardType;
            }
        }

        return null;
    }

    /**
     * Get display name for card type
     * @param {string} cardType - Card type
     * @returns {string} Display name
     */
    getCardDisplayName(cardType) {
        const displayNames = {
            'hospitalizations': 'New Hospitalizations & Re-Hospitalizations',
            'DCERData': 'DC / ER',
            'insuranceData': 'Per Insurance',
            'returnsData': 'Returned / Didn\'t Return',
            'ninetyDaysData': '90 Days Analysis',
            'floorsData': 'Per Floor',
            'doctorData': 'Per Doctor',
            'daysData': 'Per Day',
            'dxData': 'Per Diagnosis',
            'shiftData': 'Per Shift',
            'nurseData': 'Per Nurse',
            'hospitalData': 'Hospitals Residents Transferred To',
            'unplannedHospitalTransfer': 'Unplanned Transfers',
            'plannedHospitalTransfer': 'Planned Transfers',
            'total': 'Total Data'
        };
        return displayNames[cardType] || cardType;
    }

    /**
     * Initialize report service with handlers
     * @param {Function} handleOnClickReport - Report generation handler
     * @param {Function} downloadExcel - Excel download handler
     */
    initializeReportService(handleOnClickReport, downloadExcel) {
        this.handleOnClickReport = handleOnClickReport;
        this.downloadExcel = downloadExcel;
    }

    /**
     * List AI Agent conversations (history) for the current user
     * Backend: GET /api/ai-agent/conversations
     */
    async getAgentConversations({ facilityIds, pageType, startDate, endDate, search, limit = 30, skip = 0 } = {}) {
        try {
            const params = new URLSearchParams();
            if (Array.isArray(facilityIds) && facilityIds.length > 0) {
                params.append('facilityIds', facilityIds.join(','));
            }
            if (pageType) params.append('pageType', pageType);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (search) params.append('search', search);
            if (limit) params.append('limit', String(limit));
            if (skip) params.append('skip', String(skip));

            const response = await api.get(`/api/ai-agent/conversations?${params.toString()}`);
            if (response?.success) return response.data || [];
            throw new Error(response?.error || 'Failed to load conversations');
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-agent',
                event: 'get_agent_conversations_error',
                extra: { statusCode: error.response?.status }
            });
            throw new Error(error.message || 'Failed to load conversations');
        }
    }

    /**
     * Get full conversation history (with messages)
     * Backend: GET /api/ai-agent/conversation/:id
     */
    async getAgentConversationHistory(conversationId) {
        try {
            const response = await api.get(`/api/ai-agent/conversation/${conversationId}`);
            if (response?.success) return response.data;
            throw new Error(response?.error || 'Failed to load conversation');
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-agent',
                event: 'get_agent_conversation_history_error',
                extra: { conversationId, statusCode: error.response?.status }
            });
            if (error.response?.status === 404) throw new Error('Conversation not found');
            throw new Error(error.message || 'Failed to load conversation');
        }
    }

    /**
     * Rename an AI Agent conversation
     * Backend: PATCH /api/ai-agent/conversation/:id
     */
    async renameAgentConversation(conversationId, title) {
        try {
            const response = await api.patch(`/api/ai-agent/conversation/${conversationId}`, { title });
            if (response?.success) return response.data;
            throw new Error(response?.error || 'Failed to rename conversation');
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-agent',
                event: 'rename_agent_conversation_error',
                extra: { conversationId, statusCode: error.response?.status }
            });
            throw new Error(error.message || 'Failed to rename conversation');
        }
    }

    /**
     * Soft-delete every conversation for the current user. Optionally scoped
     * to a single pageType so "Clear all" matches the on-screen list.
     * Backend: DELETE /api/ai-agent/conversations
     */
    async deleteAllAgentConversations({ pageType } = {}) {
        try {
            const params = new URLSearchParams();
            if (pageType) params.append('pageType', pageType);
            const qs = params.toString();
            const url = `/api/ai-agent/conversations${qs ? `?${qs}` : ''}`;
            const response = await api.delete(url);
            if (response?.success) return response.data || { deletedCount: 0 };
            throw new Error(response?.error || 'Failed to clear conversations');
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-agent',
                event: 'delete_all_agent_conversations_error',
                extra: { pageType, statusCode: error.response?.status }
            });
            throw new Error(error.message || 'Failed to clear conversations');
        }
    }

    /**
     * Soft-delete an AI Agent conversation
     * Backend: DELETE /api/ai-agent/conversation/:id
     */
    async deleteAgentConversation(conversationId) {
        try {
            const response = await api.delete(`/api/ai-agent/conversation/${conversationId}`);
            if (response?.success) return response;
            throw new Error(response?.error || 'Failed to delete conversation');
        } catch (error) {
            logErrorToSentry(error, {
                service: 'ai-agent',
                event: 'delete_agent_conversation_error',
                extra: { conversationId, statusCode: error.response?.status }
            });
            throw new Error(error.message || 'Failed to delete conversation');
        }
    }
}

// Create and export a singleton instance
const aiChatService = new AIChatService();
export default aiChatService;
